import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Chip,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockIcon from '@mui/icons-material/Lock';

import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SubjectIcon from '@mui/icons-material/Subject';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ListIcon from '@mui/icons-material/List';
import SchoolIcon from '@mui/icons-material/School';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ChangePasswordDialog from './components/ChangePasswordDialog';

const Navbar = ({ onNavClick, handleLogout, activeView = "home" }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(activeView || "home");
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleNavClick = (section) => {
    setSelectedSection(section);
    if (onNavClick) {
      onNavClick(section);
    }
    setDrawerOpen(false);
  };

  React.useEffect(() => {
    if (activeView && activeView !== selectedSection) {
      setSelectedSection(activeView);
    }
  }, [activeView, selectedSection]);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    handleLogout();
  };

  const handleChangePasswordClick = () => {
    handleUserMenuClose();
    setChangePasswordOpen(true);
  };

  // Get user data from localStorage
  const getUserType = () => {
    try {
      const userType = localStorage.getItem('userType');
      return userType || 'User';
    } catch (error) {
      return 'User';
    }
  };

  const getUserName = () => {
    try {
      const userName = localStorage.getItem('userName');
      return userName || 'User';
    } catch (error) {
      return 'User';
    }
  };

  const userType = getUserType();
  const userName = getUserName();

  const allNavItems = [
    { key: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
    { key: "home", label: "Attendance", icon: <AssessmentIcon /> },
    { key: "students", label: "Students", icon: <PeopleIcon /> },
    { key: "test", label: "Tests", icon: <AssignmentIcon /> },
    { key: "subject", label: "Subjects", icon: <SubjectIcon /> },
    { key: "topics", label: "Topics", icon: <ListIcon /> },
    { key: "campus", label: "Campus/Batch", icon: <BusinessIcon /> },
    { key: "users", label: "Users", icon: <PeopleIcon /> },
    { key: "teachers", label: "Teachers", icon: <SchoolIcon /> },
    { key: "report", label: "Reports", icon: <AssessmentIcon /> },
  ];

  // Filter nav items based on user type
  const navItems = userType === 'Admin' 
    ? allNavItems // Admin sees all tabs
    : userType === 'President'
    ? allNavItems.filter(item => ['dashboard', 'report'].includes(item.key)) // President sees only Dashboard and Reports
    : allNavItems.filter(item => !['users', 'campus', 'teachers'].includes(item.key)); // Regular users don't see Users, Campus, or Teachers

  return (
    <>
      <AppBar 
        position="static" 
        sx={{
          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '60px', md: '70px' }, px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Logo and Brand */}
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box
              component="img"
              src="/img_1.png"
              alt="Logo"
              sx={{
                width: { xs: 35, sm: 40, md: 45 },
                height: { xs: 35, sm: 40, md: 45 },
                mr: { xs: 1, sm: 1.5, md: 2 },
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#fff',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '0.5px',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                  display: { xs: 'none', sm: 'block' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Student Management System
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 400,
                  letterSpacing: '1px',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                  display: { xs: 'none', sm: 'block' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                Anjuman Khuddam UL Quran
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#fff',
                  fontSize: '1rem',
                  display: { xs: 'block', sm: 'none' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                SMS
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ 
            display: { xs: 'none', xl: 'flex' }, 
            gap: 1, 
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflow: 'hidden'
          }}>
            {navItems.map((item) => (
              <Button
                key={item.key}
                startIcon={item.icon}
                onClick={() => handleNavClick(item.key)}
                sx={{
                  color: selectedSection === item.key ? '#fff' : 'rgba(255,255,255,0.8)',
                  backgroundColor: selectedSection === item.key ? 'rgba(255,255,255,0.15)' : 'transparent',
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  fontWeight: selectedSection === item.key ? 600 : 500,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: 'rgba(255,255,255,0.2)' }} />
            
            {/* User Profile Section with Dropdown */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              px: 2, 
              py: 1,
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
            onClick={handleUserMenuOpen}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 120
                }}
              >
                {userName}
              </Typography>
            </Box>
          </Box>

                     {/* User Dropdown Menu */}
           <Menu
             anchorEl={userMenuAnchor}
             open={Boolean(userMenuAnchor)}
             onClose={handleUserMenuClose}
             anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'right',
             }}
             transformOrigin={{
               vertical: 'top',
               horizontal: 'right',
             }}
             PaperProps={{
               sx: {
                 mt: 1,
                 minWidth: 120,
                 backgroundColor: '#fff',
                 borderRadius: '12px',
                 boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                 border: '1px solid rgba(0,0,0,0.08)',
                 '& .MuiMenuItem-root': {
                   py: 1.5,
                   px: 2,
                   fontSize: '0.9rem',
                   fontWeight: 500,
                   color: '#333',
                   '&:hover': {
                     backgroundColor: 'rgba(76, 175, 80, 0.08)',
                     color: '#2E7D32'
                   }
                 }
               }
             }}
           >
             <MenuItem onClick={handleChangePasswordClick}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                 <LockIcon sx={{ fontSize: 20, color: '#4CAF50' }} />
                 <Typography sx={{ color: '#333', fontWeight: 500 }}>
                   Change Password
                 </Typography>
               </Box>
             </MenuItem>
             <Divider sx={{ my: 0.5 }} />
             <MenuItem onClick={handleLogoutClick}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                 <Box sx={{ 
                   width: 20, 
                   height: 20, 
                   display: 'flex', 
                   alignItems: 'center', 
                   justifyContent: 'center',
                   color: '#d32f2f'
                 }}>
                   🚪
                 </Box>
                 <Typography sx={{ color: '#d32f2f', fontWeight: 500 }}>
                   Logout
                 </Typography>
               </Box>
             </MenuItem>
           </Menu>

          {/* Mobile/Tablet Menu Button */}
          <IconButton
            color="inherit"
            edge="end"
            onClick={toggleDrawer(true)}
            sx={{ 
              display: { xs: 'block', xl: 'none' },
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '10px',
              p: 1,
              ml: 1,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
            color: '#fff'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <img 
              src="/img_1.png" 
              alt="Logo" 
              style={{ 
                width: 40, 
                height: 40, 
                marginRight: 16,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }} 
            />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
              Menu
            </Typography>
          </Box>
          
          {/* User Profile Section in Mobile with Dropdown */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            p: 2,
            mb: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
          }}
          onClick={handleUserMenuOpen}
          >
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40, 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#fff', 
                fontWeight: 600, 
                fontSize: '0.95rem',
                lineHeight: 1.2
              }}
            >
              {userName}
            </Typography>
          </Box>
          
          <List sx={{ pt: 0 }}>
            {navItems.map((item) => (
              <ListItem
                key={item.key}
                button
                onClick={() => handleNavClick(item.key)}
                selected={selectedSection === item.key}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  backgroundColor: selectedSection === item.key ? 'rgba(255,255,255,0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.25)'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ color: selectedSection === item.key ? '#fff' : 'rgba(255,255,255,0.8)', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  sx={{ 
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedSection === item.key ? 600 : 500,
                      fontSize: '0.95rem'
                    }
                  }}
                />
                {selectedSection === item.key && (
                  <Chip 
                    label="Active" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                )}
              </ListItem>
            ))}
                    </List>
         

        </Box>
      </Drawer>

      {/* Mobile User Dropdown Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
            '& .MuiMenuItem-root': {
              py: 2,
              px: 2.5,
              fontSize: '1rem',
              fontWeight: 500,
              color: '#333',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                color: '#2E7D32'
              }
            }
          }
        }}
      >
        <MenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: '#2E7D32',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                {userName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                {userType}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={handleChangePasswordClick}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LockIcon sx={{ fontSize: 24, color: '#4CAF50' }} />
            <Typography sx={{ color: '#333', fontWeight: 500, fontSize: '1rem' }}>
              Change Password
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 24, 
              height: 24, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#d32f2f'
            }}>
              🚪
            </Box>
            <Typography sx={{ color: '#d32f2f', fontWeight: 500, fontSize: '1rem' }}>
              Logout
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={changePasswordOpen} 
        onClose={() => setChangePasswordOpen(false)} 
      />
    </>
  );
};

export default Navbar;
